import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, X, ShieldCheck, Cpu, Database, Users, Wrench, Gauge } from "lucide-react";

const MethodCard = ({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof ShieldCheck;
  title: string;
  body: string;
}) => (
  <div className="border border-border/60 bg-card/60 rounded-lg p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5 text-primary shrink-0" />
      <p className="text-[11px] font-mono uppercase tracking-wider font-bold text-foreground">
        {title}
      </p>
    </div>
    <p className="text-[11px] text-foreground/75 leading-relaxed">{body}</p>
  </div>
);

const RatingMethodologyCallout = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-mono uppercase tracking-wider text-primary hover:text-primary-foreground hover:bg-primary border border-primary/50 rounded-md px-3 py-1.5 transition-all"
      >
        <Info className="w-3.5 h-3.5" />
        How we rate — see the math
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mt-3"
          >
            <div className="border border-primary/30 bg-gradient-to-br from-primary/[0.05] to-accent/[0.04] rounded-xl p-4 sm:p-5 shadow-lg">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
                  <h3 className="text-sm sm:text-base font-display font-bold text-foreground">
                    How ohm&amp;ply rates every product
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-muted-foreground hover:text-foreground p-1"
                  aria-label="Close methodology"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-foreground/85 leading-relaxed mb-4">
                Our ratings are{" "}
                <span className="text-primary font-bold">
                  not opinions, fanboy votes, or AI guesses
                </span>
                . Each score is produced by a deterministic, rule-based scoring engine that
                combines published engineering specs, real-world durability data, installer
                consensus, and verified owner reviews. The same product on the same data always
                produces the same score — and inside any comparison you can click{" "}
                <span className="font-mono text-primary">"Why?"</span> on a rating row to see
                exactly which factors moved the number.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <MethodCard
                  icon={Cpu}
                  title="Engineering math"
                  body={`Subs: Thiele/Small, Xmax, Vd, motor mass, size-class anchors so an 8" isn't penalized vs an 18". Amps: impedance doubling, fuse-vs-RMS coherence, watts-per-pound believability, low-Z stability, CEA-2006 compliance. Speakers: sensitivity-first (head-unit power is ~18W/ch real-world), size-normalized RMS, FR width, mounting-depth realism. Tweeters: measured FR flatness and breakup behavior first, crossover freedom, ferrofluid/neo cooling — dome material is only a tiebreaker. Alternators: idle output weighted over peak dyno numbers, idle-to-peak ratio, hairpin/6-phase stator, rectifier and regulator quality, dollars-per-idle-amp value.`}
                />
                <MethodCard
                  icon={Database}
                  title="Verified specs"
                  body="Manufacturer sheets cross-checked against Crutchfield, Sonic Electronix, Parts Express, and authorized retailers. RMS always wins over peak."
                />
                <MethodCard
                  icon={Users}
                  title="Forum & owner consensus"
                  body={`Sentiment mined from DIYMA, SMD Forum, r/CarAV, and CarAudio.com — weighted for "widespread" vs "isolated" reports, with anti-fanboy guards.`}
                />
                <MethodCard
                  icon={Wrench}
                  title="Installer feedback"
                  body="Long-term reliability and abuse-tolerance signals from working installers and SPL/SQ competitors who actually push these products."
                />
                <MethodCard
                  icon={Gauge}
                  title="Brand reliability tier"
                  body="A maintained reputation table for 70+ brands based on warranty rates, failure patterns, and build consistency over years."
                />
                <MethodCard
                  icon={ShieldCheck}
                  title="Anti-manipulation"
                  body="Review aggregates are filtered for fake-review patterns, marketing hype, and incorrect user setups (under-powered or over-powered installs)."
                />
              </div>

              <div className="border-t border-border/60 pt-3">
                <p className="text-[10px] font-mono text-accent uppercase tracking-wider font-bold mb-2">
                  The 4 category scores
                </p>
                <ul className="space-y-1.5 text-[11px] text-foreground/85 leading-relaxed">
                  <li>
                    <span className="text-primary font-bold">Reliability</span> — brand tier +
                    voice-coil thermal headroom + suspension design + forum failure reports.
                  </li>
                  <li>
                    <span className="text-primary font-bold">Abuse Tolerance</span> — RMS vs
                    size-class anchor, motor mass, Xmax, cooling, and real-world over-power
                    survival reports.
                  </li>
                  <li>
                    <span className="text-primary font-bold">Build Quality</span> — basket type,
                    surround/spider materials, terminal hardware, and consensus on long-term
                    construction.
                  </li>
                  <li>
                    <span className="text-primary font-bold">User Reviews</span> — live-pulled,
                    weighted by source credibility and review volume, with low-confidence flagging
                    when data is thin.
                  </li>
                </ul>
              </div>

              <p className="text-[10px] font-mono text-muted-foreground mt-3 italic">
                Goal: engineering accuracy, real-world durability, installer trust — not
                popularity. Every rating row inside a comparison has a "Why?" button so you can
                audit the math yourself.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RatingMethodologyCallout;
